import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Skeleton, Alert } from 'antd';
import { PromptWrapper, DisclaimerFooter, MessageContainer, MessageText } from '../styled';
import Header from '../Header';
import { useViewShare } from '../Share/hooks/useViewShare';
import { ContinueConversationBanner } from './ContinueConversationBanner';
import { ScrollWrapper } from '../Events/styled';
import { useProcessedEvents, ProcessedNexaEvent } from '../Events/utils';
import EventRecord from '../Events/EventRecord';
import { initNexaStore, isReportPanelOpenAtom } from '../NexaStore';
import { useListReportVersions } from '../hooks/useReportApi';

function SharedView() {
  const { id: shareId } = useParams<{ id: string }>();
  const { events, state, loading, error } = useViewShare(shareId);
  const setIsReportPanelOpen = useSetAtom(isReportPanelOpenAtom);

  // Fetch report versions for this shared view (hook pulls sessionId from store)
  const {
    versions,
    isFetching: isFetchingVersions,
    refetch: refetchVersions,
  } = useListReportVersions();

  // Initialize store with shared session data so dashboard links and state work
  useEffect(() => {
    if (events.length > 0 && state) {
      // Events are already properly formatted from useViewShare with correct structure
      initNexaStore({
        events: events as any,
        id: shareId,
        dashboardConfigMap: state,
      });
      // Trigger report versions fetch now that store is initialized with sessionId
      refetchVersions();
    }
  }, [events, state, shareId]);

  // Auto-open report panel if report versions are available (shared view only)
  useEffect(() => {
    if (!isFetchingVersions && versions.length > 0) {
      setIsReportPanelOpen(true);
    }
  }, [isFetchingVersions, versions, setIsReportPanelOpen]);

  // Process events similar to regular EventList
  const processedEvents: ProcessedNexaEvent[] = useProcessedEvents(events as any, false);

  if (loading) {
    return (
      <PromptWrapper>
        <Header />
        <MessageContainer>
          <Skeleton active />
        </MessageContainer>
        <DisclaimerFooter>
          Conviva&apos;s Nexa is built on LLM technology. LLMs can generate inaccurate output.
        </DisclaimerFooter>
      </PromptWrapper>
    );
  }

  if (error) {
    const errorMessage =
      (error as any)?.response?.data?.detail?.message ||
      (error as any)?.message ||
      'Failed to load shared session';
    const isAccessDenied =
      errorMessage.includes('Access denied') || (error as any)?.response?.status === 403;

    return (
      <PromptWrapper>
        <Header />
        <MessageContainer>
          <Alert
            message={isAccessDenied ? 'Access Denied' : 'Error'}
            description={
              isAccessDenied
                ? 'You do not have permission to view this shared session. It may be from a different organization or product.'
                : errorMessage
            }
            type="error"
            showIcon
          />
        </MessageContainer>
        <DisclaimerFooter>
          Conviva&apos;s Nexa is built on LLM technology. LLMs can generate inaccurate output.
        </DisclaimerFooter>
      </PromptWrapper>
    );
  }

  return (
    <PromptWrapper>
      <Header />
      <ScrollWrapper style={{ paddingBottom: 50 }}>
        {processedEvents.length === 0 ? (
          <MessageContainer>
            <MessageText>This shared session has no events.</MessageText>
          </MessageContainer>
        ) : (
          processedEvents.map((entry) => {
            const {
              id = '-',
              author,
              type,
              value,
              timestamp,
              isLastAgentInSequence,
              thought,
              hasNonThoughtAfter,
              precedingUserQuery,
              functionCalls,
            } = entry;
            return (
              <EventRecord
                id={id}
                author={author}
                type={type}
                value={value}
                key={id}
                functionCalls={functionCalls}
                isLastAgentInSequence={isLastAgentInSequence}
                thought={thought}
                hasNonThoughtAfter={hasNonThoughtAfter}
                precedingUserQuery={precedingUserQuery}
              />
            );
          })
        )}
      </ScrollWrapper>
      <ContinueConversationBanner shareId={shareId!} />
      <DisclaimerFooter>
        Conviva&apos;s Nexa is built on LLM technology. LLMs can generate inaccurate output.
      </DisclaimerFooter>
    </PromptWrapper>
  );
}

export default SharedView;
